import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRegistrationStatus } from '../hooks/useRegistrationStatus';
import type { RegistrationStatusResult } from '../hooks/useRegistrationStatus';
import { getPendingSignup } from '../utils/pendingSignup';
import { useTranslation } from '../i18n/react';
import { DS2 } from '../theme/designTokens';
import { AuthPageShell } from '../components/ui/AuthPageShell';
import { AuthBrandMark } from '../components/ui/AuthBrandMark';
import { AuthHeading } from '../components/ui/AuthHeading';
import { AuthBody } from '../components/ui/AuthBody';
import { LOGIN_GLOW_1, LOGIN_GLOW_2 } from '../screens/LoginScreen';

/**
 * Route guards for App.tsx's router. Extracted to their own module (rather
 * than living inline in App.tsx) so they're unit-testable without pulling
 * in App.tsx's full screen graph (GameScreen/Phaser scenes, admin screens,
 * etc.) — see authGuards.test.tsx.
 *
 * FAIL-CLOSED on registration status: whenever a decision depends on
 * whether registration is complete, a 'loading' status renders a neutral
 * spinner — never the protected content and never a guessed redirect. This
 * is the concrete "does not silently fail open into protected app routes"
 * guarantee: an incomplete user can't flash protected content while /me is
 * still resolving. On a persistent /me failure the status stays 'loading'
 * (fail-closed spinner), and the hook releases its in-flight lock so the
 * next navigation retries — no redirect loop, no leak.
 */

function RouteSpinner() {
  // Localized accessible name, resolved at render time so a runtime language
  // switch relabels a visible spinner without any behavioral side effects.
  const { t } = useTranslation('auth');
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#1A1A1A' }}>
      <div
        aria-label={t('guard.loading')}
        role="status"
        style={{
          width: 32, height: 32, borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.10)', borderTopColor: '#c9a84c',
          animation: 'spin 0.8s linear infinite',
        }}
      />
    </div>
  );
}

/**
 * Shown when the /me registration check exhausts its bounded retries. Simple
 * Auth-family error state with a Retry button that starts a fresh request.
 * Never renders protected content and never redirects — status is unknown.
 */
function RegistrationErrorState({ onRetry }: { onRetry: () => void }) {
  // Copy resolves at render time (never stored in state): a visible error
  // screen relabels immediately on a runtime language switch, and switching
  // language triggers no retry, reload, logout, or network request.
  const { t } = useTranslation('auth');
  return (
    <AuthPageShell glow1={LOGIN_GLOW_1} glow2={LOGIN_GLOW_2}>
      <AuthBrandMark />
      <AuthHeading>{t('guard.errorTitle')}</AuthHeading>
      <AuthBody>{t('guard.errorBody')}</AuthBody>
      <div style={{ width: '100%', marginTop: 24 }}>
        <button
          type="button"
          onClick={onRetry}
          style={{
            width: '100%', height: 39, boxSizing: 'border-box', display: 'flex', alignItems: 'center',
            justifyContent: 'center', borderRadius: 11.25, background: DS2.gradient.gold,
            border: '0.75px solid #C9A84C', boxShadow: '0px 22.5px 60px -22.5px rgba(201, 168, 76, 0.45)',
            color: '#FFFFFF', fontFamily: DS2.font.body, fontWeight: 500, fontSize: 16.5, cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('guard.retry')}
        </button>
      </div>
    </AuthPageShell>
  );
}

/** Renders the spinner (loading) or Retry error state (exhausted). Returns
 *  null when status is a concrete complete/incomplete decision, letting the
 *  caller proceed. Centralizes the fail-closed "unknown status" handling. */
function unknownStatusFallback(registration: RegistrationStatusResult): ReactNode | null {
  if (registration.status === 'loading') return <RouteSpinner />;
  if (registration.status === 'error')   return <RegistrationErrorState onRetry={registration.retry} />;
  return null;
}

export function PublicOnly({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? <Navigate to="/home" replace /> : <>{children}</>;
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const registration = useRegistrationStatus();
  if (!isLoggedIn) return <Navigate to="/" replace />;
  // Fail-closed on unknown status (loading → spinner, exhausted → Retry).
  // Never the protected children (would flash for an incomplete user) and
  // never a guessed redirect.
  const fallback = unknownStatusFallback(registration);
  if (fallback) return <>{fallback}</>;
  // A Firebase account that never finished Step 2 (username) must not bypass
  // onboarding — resume it there.
  if (registration.status === 'incomplete') return <Navigate to="/signup/username" replace />;
  return <>{children}</>;
}

/**
 * Step 1 of email registration ("Choose Your Email and Password"). A
 * logged-out visitor sees it immediately (no /me needed). A logged-in user
 * is either resuming (incomplete → Step 2) or already done (complete →
 * /home); we wait on 'loading' rather than guess.
 */
export function SignupStep1Guard({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const registration = useRegistrationStatus();
  if (!isLoggedIn) return <>{children}</>;
  const fallback = unknownStatusFallback(registration);
  if (fallback) return <>{fallback}</>;
  if (registration.status === 'incomplete') return <Navigate to="/signup/username" replace />;
  return <Navigate to="/home" replace />; // complete
}

/**
 * Step 2 of email registration ("Choose your Username") — also the resume
 * destination for an incomplete-registration user (RequireAuth/Step1Guard
 * redirect here). Reachable in exactly two situations: mid-flow (not yet
 * logged in, Step 1's credentials held in memory — see utils/pendingSignup)
 * or resuming (already Firebase-authenticated, no backend username yet).
 * Anything else — a refresh/direct hit with neither — has nothing to resume
 * and goes back to Step 1.
 */
export function SignupStep2Guard({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useAuth();
  const registration = useRegistrationStatus();
  if (!isLoggedIn) {
    // Not logged in: this is only valid mid-flow, i.e. Step 1 handed us
    // credentials in memory. Registration status is irrelevant here (no
    // account yet), so no /me dependency and no loading state to wait on.
    return getPendingSignup() ? <>{children}</> : <Navigate to="/signup/email" replace />;
  }
  // Logged in: resuming. Wait on unknown status (spinner / Retry), send
  // completed users home, let incomplete users finish choosing a username.
  const fallback = unknownStatusFallback(registration);
  if (fallback) return <>{fallback}</>;
  if (registration.status === 'complete') return <Navigate to="/home" replace />;
  return <>{children}</>; // incomplete
}

export function AuthAwareFallback() {
  const { isLoggedIn } = useAuth();
  return <Navigate to={isLoggedIn ? '/home' : '/'} replace />;
}
